import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Info, Sparkles } from "lucide-react";

const SPLITS = [
    { value: "upper_lower", label: "Upper / Lower (4 days)", desc: "Upper + Lower sessions, ideal if you can train 4×/week." },
    { value: "full_body", label: "Full Body (3 days)", desc: "Hits everything each session, only 3 sessions per week." },
];

// Only relevant when split = "upper_lower". Drives the schedule-aware Next
// Session card on the Dashboard so it knows whether today is a rest day.
const UL_VARIANTS = [
    {
        value: "ul_distributed",
        label: "Distributed rest",
        pattern: "U · L · Rest · U · Rest · L · Rest",
        desc: "4 lifting days with a single rest day between each pair. Easier on recovery, fits most life schedules.",
    },
    {
        value: "ul_paired",
        label: "Paired with weekend off",
        pattern: "U · L · Rest · U · L · Rest · Rest",
        desc: "Two back-to-back upper/lower pairs Mon–Fri, weekend completely off.",
    },
];

const GOALS = [
    { value: "gain", label: "Build muscle", desc: "Maintenance calories or mild surplus." },
    { value: "recomp", label: "Recomposition", desc: "Maintenance + protein. Build muscle, lose fat." },
    { value: "fatloss", label: "Lose fat", desc: "Modest calorie deficit + 6–10k daily steps." },
];

const EXPERIENCES = [
    { value: "beginner", label: "Beginner", desc: "Under 2 years of consistent training." },
    { value: "intermediate", label: "Intermediate", desc: "Between 2–5 years under the bar." },
    { value: "advanced", label: "Advanced", desc: "5+ years of dedicated, structured lifting." },
];

// Tiny step-goal coach — flips a no-fluff quip under the slider as the user drags.
// Encouragement scales with intent: 4k = call-out, 6k = floor, 10k = unlock, 12k+ = elite.
function stepGoalQuip(steps) {
    if (steps <= 4000) return "You can do better..";
    if (steps <= 5000) return "Keep climbing.";
    if (steps <= 6000) return "The bare minimum.";
    if (steps <= 7000) return "Warming up.";
    if (steps <= 8000) return "Solid baseline.";
    if (steps <= 9000) return "Almost there.";
    if (steps <= 10000) return "Now we're talking!";
    if (steps <= 11000) return "Built different.";
    if (steps <= 12000) return "Wired for work.";
    if (steps <= 13000) return "Recovery accelerator.";
    if (steps <= 14000) return "Untouchable.";
    return "Maniac level. Respect.";
}

export default function Account() {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        experience: user.experience || "intermediate",
        goal: user.goal || "recomp",
        split: user.split || "upper_lower",
        step_goal: user.step_goal || 8000,
        schedule_variant: user.schedule_variant || "ul_distributed",
    });
    const [saving, setSaving] = useState(false);

    // Mirrors the onboarding "Recommended for you" rule: beginners and
    // (fatloss + not-advanced) lifters do better on Full Body 3-day to keep
    // weekly frequency high while session volume stays manageable.
    const recommendedSplit =
        form.experience === "beginner" || (form.goal === "fatloss" && form.experience !== "advanced")
            ? "full_body"
            : "upper_lower";
    const showBeginnerNudge = form.experience === "beginner" && form.split !== recommendedSplit;

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/onboarding", form);
            await checkAuth();
            toast.success("Saved");
            navigate("/dashboard");
        } catch (e) {
            toast.error("Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-8" data-testid="account-page">
            <div>
                <div className="label-eyebrow text-accent-red">Settings</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Account</h1>
                <p className="mt-2 text-sm text-muted-foreground">Change your split, mode, or experience anytime. Switching the split applies to your next session.</p>
            </div>

            <div className="tactical-card p-6 space-y-3">
                <div className="label-eyebrow text-accent-red">Profile</div>
                <div className="text-sm"><span className="text-muted-foreground">Name:</span> {user.name}</div>
                <div className="text-sm"><span className="text-muted-foreground">Email:</span> {user.email}</div>
                <div className="text-sm"><span className="text-muted-foreground">Subscription:</span> {user.subscription_status}{user.subscription_plan ? ` · ${user.subscription_plan}` : ""}</div>
                {user.subscription_ends_at && (
                    <div className="text-sm"><span className="text-muted-foreground">Next renewal / expires:</span> {new Date(user.subscription_ends_at).toLocaleDateString()}</div>
                )}
                {user.subscription_status === "active" && user.stripe_subscription_id && (
                    <button
                        onClick={async () => {
                            try {
                                const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
                                window.location.href = data.url;
                            } catch (e) { toast.error("Could not open billing portal"); }
                        }}
                        data-testid="account-manage-sub"
                        className="mt-2 rounded-sm border border-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red hover:text-white"
                    >
                        Manage subscription
                    </button>
                )}
            </div>

            <div className="tactical-card p-6 space-y-4">
                <div className="label-eyebrow text-accent-red">Experience</div>
                <p className="text-xs text-muted-foreground">Pick the band that best matches the time you've actually been training consistently. We use this to flavour split recommendations and to gate the slot-reorder feature.</p>
                <div className="grid gap-3 md:grid-cols-3">
                    {EXPERIENCES.map(e => (
                        <button key={e.value} data-testid={`acct-exp-${e.value}`} onClick={() => setForm({ ...form, experience: e.value })}
                            className={`text-left rounded-sm border p-4 transition-colors ${form.experience === e.value ? "border-accent-red bg-accent-red/10" : "border-border bg-card hover:bg-secondary"}`}>
                            <div className="font-display text-lg font-black uppercase">{e.label}</div>
                            <div className="mt-1 text-xs text-muted-foreground" data-testid={`acct-exp-desc-${e.value}`}>{e.desc}</div>
                        </button>
                    ))}
                </div>
                <div className="flex items-start gap-2 rounded-sm border border-border bg-secondary/30 p-3 text-xs text-foreground/80" data-testid="acct-exp-restrictions">
                    <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
                    <p>
                        <span className="font-bold uppercase tracking-wider">Heads up.</span>{" "}
                        Beginners are recommended the <span className="font-bold">Full Body</span> split for higher per-muscle frequency and faster motor learning. <span className="font-bold">Slot reordering on the Workouts page is Advanced-only</span> — the default exercise selection and order is already optimised for efficiency.
                    </p>
                </div>
            </div>

            <div className="tactical-card p-6 space-y-4">
                <div className="label-eyebrow text-accent-red">Goal / Mode</div>
                <div className="grid gap-3 md:grid-cols-3">
                    {GOALS.map(g => (
                        <button key={g.value} data-testid={`acct-goal-${g.value}`} onClick={() => setForm({ ...form, goal: g.value })}
                            className={`text-left rounded-sm border p-4 ${form.goal === g.value ? "border-accent-red bg-accent-red/10" : "border-border bg-card hover:bg-secondary"}`}>
                            <div className="font-display text-lg font-black uppercase">{g.label}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{g.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="tactical-card p-6 space-y-4">
                <div className="flex items-center justify-between gap-2">
                    <div className="label-eyebrow text-accent-red">Training Split</div>
                    {form.experience === "beginner" && (
                        <span className="flex items-center gap-1 rounded-sm border border-accent-red/60 bg-accent-red/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-accent-red" data-testid="acct-beginner-rec-pill">
                            <Sparkles className="h-3 w-3" /> Full Body recommended
                        </span>
                    )}
                </div>
                {showBeginnerNudge && (
                    <div className="flex items-start gap-2 rounded-sm border border-accent-red/40 bg-accent-red/5 p-3 text-xs text-foreground/85" data-testid="acct-beginner-nudge">
                        <Info className="mt-0.5 h-4 w-4 flex-none text-accent-red" />
                        <p>
                            <span className="font-bold uppercase tracking-wider">Heads up · beginner.</span>{" "}
                            Full Body (3 days) is the recommended starting split. Higher frequency per muscle, lower per-session volume, faster motor-learning. You can switch later once intermediate.
                        </p>
                    </div>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                    {SPLITS.map(s => (
                        <button key={s.value} data-testid={`acct-split-${s.value}`} onClick={() => setForm({ ...form, split: s.value })}
                            className={`text-left rounded-sm border p-4 ${form.split === s.value ? "border-accent-red bg-accent-red/10" : "border-border bg-card hover:bg-secondary"}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="font-display text-lg font-black uppercase">{s.label}</div>
                                {recommendedSplit === s.value && form.experience === "beginner" && (
                                    <span className="rounded-sm bg-accent-red px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" data-testid={`acct-rec-badge-${s.value}`}>Recommended</span>
                                )}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">{s.desc}</div>
                        </button>
                    ))}
                </div>

                {/* Schedule variant — only relevant on Upper/Lower because Full
                    Body's 3-day cycle has no real alternative. The Dashboard's
                    Next Session card uses this to know which 7-day pattern to
                    follow. */}
                {form.split === "upper_lower" && (
                    <div className="space-y-2 border-t border-border pt-4" data-testid="acct-ul-variant">
                        <div className="label-eyebrow text-muted-foreground">Weekly pattern · pick one</div>
                        <p className="text-xs text-muted-foreground">Which Upper/Lower rhythm matches how you actually train? We use this to drive the Dashboard's Next Session card and rest-day messaging.</p>
                        <div className="grid gap-3 md:grid-cols-2">
                            {UL_VARIANTS.map(v => (
                                <button
                                    key={v.value}
                                    data-testid={`acct-ul-variant-${v.value}`}
                                    onClick={() => setForm({ ...form, schedule_variant: v.value })}
                                    className={`text-left rounded-sm border p-4 transition-colors ${form.schedule_variant === v.value ? "border-accent-red bg-accent-red/10" : "border-border bg-card hover:bg-secondary"}`}
                                >
                                    <div className="font-display text-base font-black uppercase">{v.label}</div>
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-accent-red">{v.pattern}</div>
                                    <div className="mt-1 text-xs text-muted-foreground">{v.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="tactical-card p-6 space-y-3">
                <div className="label-eyebrow text-accent-red">Daily Step Goal</div>
                <input type="range" min="4000" max="15000" step="1000" value={form.step_goal}
                    onChange={e => setForm({ ...form, step_goal: parseInt(e.target.value) })}
                    data-testid="acct-steps" className="w-full accent-[#ff3b30]" />
                <div className="flex items-baseline justify-between gap-3">
                    <div className="font-display text-3xl font-black">{form.step_goal.toLocaleString()}</div>
                    <span data-testid="acct-steps-quip" className="text-xs font-bold uppercase tracking-wider text-accent-red">
                        {stepGoalQuip(form.step_goal)}
                    </span>
                </div>
            </div>

            <button onClick={save} disabled={saving} data-testid="acct-save"
                className="w-full rounded-sm bg-accent-red px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60 sm:w-auto">
                {saving ? "Saving…" : "Save changes"}
            </button>
        </div>
    );
}
